import styled from 'styled-components';
import useStore, { actions } from '../../store/useStore';
import { modelVersions } from '../../services/constants';
import { reuseTypes } from '../../services/parameters';
import { drainReuseKeys } from '../../services/constants';
import SelectList from '../Input/SelectList';
import RadioGroup from '../Input/RadioGroup';
import NumberInput from '../Input/NumberInput';
import Checkbox from '../Input/Checkbox';
import { reuseDrainwaterPossible } from '../../store/helpers';
import Button, { RestartButton } from '../Button';

const SettingsContainer = styled.div``;

const SubSettingsContainer = styled.div`
    padding-left: 16px;
    margin-bottom: 16px;
`;

const StyledSelect = styled(SelectList)`
    width: 300px;
`;

const StartSettings = ({ modelVersion, className }) => {
    const { store, dispatch } = useStore();
    const {
        selectedCropParameter,
        selectedStage,
        selectedSubstrate,
        reuseDrainwaterValue,
        chlorineValue,
        summerWinter,
        ureumValue,
        dropWaterValue,
    } = store.input;

    const cropParameters = store.config?.cropList;
    if (!cropParameters) {
        return 'Loading...';
    }
    const cropOptions = cropParameters.map((param) => ({
        value: param.crop,
        label: param.crop,
    }));

    const substrateOptions =
        selectedCropParameter?.substrates?.map((substrate) => ({
            label: substrate.substrate,
            value: substrate,
        })) || [];

    const drainType = reuseDrainwaterValue.reuse
        ? drainReuseKeys.reuse
        : drainReuseKeys.free;

    let stageOptions = [];
    if (modelVersion === modelVersions.substraat) {
        if (
            selectedSubstrate &&
            selectedSubstrate.draintypeConfig[drainType] &&
            selectedSubstrate.draintypeConfig[drainType].phases
        ) {
            stageOptions = selectedSubstrate.draintypeConfig[
                drainType
            ].phases.map((stage) => ({
                label: stage,
                value: stage,
            }));
        }
    } else {
        if (selectedCropParameter && selectedCropParameter.cropStages) {
            stageOptions = selectedCropParameter.cropStages.map((stage) => ({
                label: stage,
                value: stage,
            }));
        }
    }

    const handleSelectCrop = (crop) => {
        const selectedParam = cropParameters.find(
            (param) => param.crop === crop
        );
        dispatch(
            actions.setInputValue('selectedCropParameter', selectedParam, true)
        );
    };

    const handleSelectStage = (stage) => {
        dispatch(actions.changeSelectedStage(stage));
    };

    const handleSelectSubstrate = (substrate) => {
        dispatch(actions.setInputValue('selectedSubstrate', substrate, true));
    };

    const setReuseDrainwaterValue = (value) => {
        dispatch(actions.setInputValue('reuseDrainwaterValue', value, true));
    };

    const setChlorineValue = (value) => {
        dispatch(actions.setInputValue('chlorineValue', value, true));
    };

    const setSummerWinterValue = (value) => {
        dispatch(actions.setInputValue('summerWinter', value, true));
    };

    const setUreumValue = (value) => {
        dispatch(actions.setInputValue('ureumValue', value, true));
    };

    const setDropWaterValue = (value) => {
        dispatch(actions.setInputValue('dropWaterValue', value, true));
    };

    const cropSelected = Boolean(selectedCropParameter?.crop);
    const substrateSelected = Boolean(selectedSubstrate);

    const drainReuseAvailable = reuseDrainwaterPossible({
        modelVersion,
        selectedCropParameter,
        selectedSubstrate,
    });

    const chlorineOptionAvailable = selectedSubstrate
        ? selectedSubstrate.chlorine
        : false;

    const summerWinterOptionAvailable = selectedCropParameter
        ? selectedCropParameter.summerWinterAvailable
        : false;

    const restart = () => {
        dispatch(actions.clearInput());
    };

    return (
        <SettingsContainer className={className}>
            <StyledSelect
                label="Gewas"
                id="Gewas"
                options={cropOptions}
                value={selectedCropParameter?.crop || ''}
                onChange={handleSelectCrop}
                placeholder=" -- maak een keuze -- "
                isClearable
            />
            {modelVersion === modelVersions.substraat && (
                <StyledSelect
                    label="Substraat"
                    id="Substraat"
                    options={substrateOptions}
                    value={selectedSubstrate}
                    onChange={handleSelectSubstrate}
                    placeholder={` -- ${
                        cropSelected
                            ? 'maak een keuze'
                            : 'selecteer eerst een gewas'
                    } -- `}
                    disabled={!cropSelected}
                />
            )}
            {Boolean(
                cropSelected &&
                    (modelVersion !== modelVersions.substraat ||
                        substrateSelected) &&
                    drainReuseAvailable
            ) && (
                <>
                    <Checkbox
                        label="Hergebruik drainwater"
                        id="drainWater"
                        value={reuseDrainwaterValue.reuse}
                        onChange={(val) =>
                            setReuseDrainwaterValue((prev) => ({
                                ...prev,
                                reuse: val,
                            }))
                        }
                        disabled={
                            !cropSelected ||
                            (modelVersion === modelVersions.substraat &&
                                !substrateSelected) ||
                            !drainReuseAvailable
                        }
                    />

                    {Boolean(
                        reuseDrainwaterValue.reuse && drainReuseAvailable
                    ) && (
                        <SubSettingsContainer>
                            <RadioGroup
                                disabled={
                                    !reuseDrainwaterValue.reuse ||
                                    !drainReuseAvailable
                                }
                                options={[
                                    {
                                        label: (
                                            <span>
                                                Vast mengpercentage{' '}
                                                <NumberInput
                                                    disabled={
                                                        !drainReuseAvailable ||
                                                        reuseDrainwaterValue.reuseType !==
                                                            reuseTypes.fixed ||
                                                        !reuseDrainwaterValue.reuse
                                                    }
                                                    width={30}
                                                    value={
                                                        reuseDrainwaterValue.fixedPercentage
                                                    }
                                                    onChange={(val) =>
                                                        setReuseDrainwaterValue(
                                                            (prev) => ({
                                                                ...prev,
                                                                fixedPercentage:
                                                                    val,
                                                            })
                                                        )
                                                    }
                                                />{' '}
                                                %
                                            </span>
                                        ),
                                        value: reuseTypes.fixed,
                                    },
                                    {
                                        label: (
                                            <span>
                                                EC regeling{' '}
                                                <NumberInput
                                                    disabled={
                                                        !drainReuseAvailable ||
                                                        reuseDrainwaterValue.reuseType !==
                                                            reuseTypes.EC ||
                                                        !reuseDrainwaterValue.reuse
                                                    }
                                                    width={30}
                                                    value={
                                                        reuseDrainwaterValue.EC
                                                    }
                                                    onChange={(val) =>
                                                        setReuseDrainwaterValue(
                                                            (prev) => ({
                                                                ...prev,
                                                                EC: val,
                                                            })
                                                        )
                                                    }
                                                />{' '}
                                                dS/m
                                            </span>
                                        ),
                                        value: reuseTypes.EC,
                                    },
                                ]}
                                onChange={(val) =>
                                    setReuseDrainwaterValue((prev) => ({
                                        ...prev,
                                        reuseType: val,
                                    }))
                                }
                                value={reuseDrainwaterValue.reuseType}
                            />
                        </SubSettingsContainer>
                    )}
                </>
            )}
            {modelVersion === modelVersions.substraat && (
                <StyledSelect
                    label="Gewasstadium"
                    id="Gewasstadium"
                    options={stageOptions}
                    value={selectedStage}
                    onChange={handleSelectStage}
                    placeholder={` -- ${
                        cropSelected && substrateSelected
                            ? 'maak een keuze'
                            : 'selecteer eerst een substraat'
                    } -- `}
                    disabled={!cropSelected || !substrateSelected}
                    isSearchable={false}
                />
            )}
            {Boolean(
                modelVersion === modelVersions.potplant &&
                    cropSelected &&
                    stageOptions
            ) && (
                <StyledSelect
                    label="Teeltfase"
                    id="Teeltfase"
                    options={stageOptions}
                    value={selectedStage}
                    onChange={handleSelectStage}
                    placeholder={` -- maak een keuze -- `}
                />
            )}
            {modelVersion === modelVersions.substraat &&
                chlorineOptionAvailable && (
                    <Checkbox
                        label="Chloride gebruik"
                        id="chlorine"
                        value={chlorineValue}
                        onChange={setChlorineValue}
                        disabled={!chlorineOptionAvailable}
                    />
                )}
            {modelVersion === modelVersions.potplant && (
                <div>
                    <Checkbox
                        label="Wordt ureum gebruikt in de druppeloplossing?"
                        id="ureum"
                        value={ureumValue.used}
                        onChange={(val) =>
                            setUreumValue((prev) => ({
                                ...prev,
                                used: val,
                            }))
                        }
                    />
                    {ureumValue.used && (
                        <SubSettingsContainer>
                            <div>
                                <NumberInput
                                    width={50}
                                    value={ureumValue.percentage}
                                    onChange={(val) =>
                                        setUreumValue((prev) => ({
                                            ...prev,
                                            percentage: val,
                                        }))
                                    }
                                />{' '}
                                %N-ureum van N-totaal
                            </div>
                            <div>
                                <NumberInput
                                    width={50}
                                    value={ureumValue.drain}
                                    onChange={(val) =>
                                        setUreumValue((prev) => ({
                                            ...prev,
                                            drain: val,
                                        }))
                                    }
                                />{' '}
                                ureum_drain
                            </div>
                        </SubSettingsContainer>
                    )}
                </div>
            )}
            <div>
                EC irrigatie{' '}
                <NumberInput
                    width={50}
                    value={dropWaterValue}
                    onChange={setDropWaterValue}
                />{' '}
                dS/m
            </div>
            {modelVersion === modelVersions.potplant &&
                summerWinterOptionAvailable && (
                    <RadioGroup
                        label="Zomer/winter"
                        id="sumerWinter"
                        options={[
                            { label: 'zomer', value: 'zomer' },
                            { label: 'winter', value: 'winter' },
                        ]}
                        value={summerWinter}
                        onChange={setSummerWinterValue}
                        style={{ marginTop: 8 }}
                    />
                )}

            <div
                style={{
                    marginTop: 32,
                    textAlign: 'right',
                    visibility: cropSelected ? 'visible' : 'hidden',
                }}
            >
                <RestartButton variant="secondary" onClick={restart}>
                    opnieuw beginnen
                </RestartButton>
            </div>
        </SettingsContainer>
    );
};

export default StartSettings;
